import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import AppSwal from '@/helpers/swal';
import { isTemplatesPoolBranch } from '@/helpers/templatesPool';

const SSE_RECONNECT_BASE_DELAY_MS = 2000;
const SSE_RECONNECT_MAX_DELAY_MS = 30000;
const SSE_INACTIVITY_TIMEOUT_MS = 20000;
const SSE_MAX_RECONNECT_ATTEMPTS = 5;
const APP_IDLE_LOGOUT_TIMEOUT_MS = 30 * 60 * 1000;
const APP_IDLE_WARNING_LEAD_MS = 60 * 1000;
const APP_IDLE_WARNING_TIMEOUT_MS = APP_IDLE_LOGOUT_TIMEOUT_MS - APP_IDLE_WARNING_LEAD_MS;
const APP_ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'mousemove'];

let sseReconnectTimer = null;
let sseInactivityTimer = null;
let sseReconnectAttempts = 0;
let sseLogoutInProgress = false;
let runtimeGuardsInitialized = false;
let runtimeVisibilityHandler = null;
let runtimeOnlineHandler = null;
let runtimeActivityHandler = null;
let appIdleLogoutTimer = null;
let appIdleWarningTimer = null;

function clearSseReconnectTimer() {
  if (sseReconnectTimer) {
    window.clearTimeout(sseReconnectTimer);
    sseReconnectTimer = null;
  }
}

function clearSseInactivityTimer() {
  if (sseInactivityTimer) {
    window.clearTimeout(sseInactivityTimer);
    sseInactivityTimer = null;
  }
}

function clearAppIdleLogoutTimer() {
  if (appIdleLogoutTimer) {
    window.clearTimeout(appIdleLogoutTimer);
    appIdleLogoutTimer = null;
  }
}

function clearAppIdleWarningTimer() {
  if (appIdleWarningTimer) {
    window.clearTimeout(appIdleWarningTimer);
    appIdleWarningTimer = null;
  }
}

function sortByLabel(a, b) {
  return getItemLabel(a).localeCompare(getItemLabel(b), undefined, { sensitivity: 'base' });
}

function getPoolLeafName(poolId) {
  const value = String(poolId || '');
  if (!value) return '';
  const parts = value.split('/').filter(Boolean);
  return parts.length ? parts[parts.length - 1] : value;
}

function getItemLabel(item) {  if (item?.type === 'resourcePool') {
    return String(getPoolLeafName(item?.poolid) || item?.name || '');
  }
  return String(
    item?.poolid ||
    item?.name ||
    item?.clusterName ||
    item?.vmid ||
    item?.hostname ||
    item?.id ||
    ''
  );
}

function normalizeClusterEntry(entry) {
  const source = entry && typeof entry === 'object' ? entry : {};
  const id = String(source.id || '').trim();
  if (!id) return null;
  return {
    ...source,
    id,
    name: String(source.name || id).trim(),
    location: String(source.location || '').trim(),
    locationUrl: String(source.locationUrl || '').trim(),
    hardware: String(source.hardware || '').trim(),
    vmStorage: String(source.vmStorage || '').trim(),
    tpmStorage: String(source.tpmStorage || '').trim(),
  };
}

function normalizeClusterList(clusters) {
  if (!Array.isArray(clusters)) return [];
  return clusters.map(normalizeClusterEntry).filter(Boolean);
}

const CLUSTERS_STORAGE_KEY = 'vmanager_proxmox_clusters';

function saveClustersToPersistence(clusters) {
  try {
    window.localStorage.setItem(CLUSTERS_STORAGE_KEY, JSON.stringify(clusters));
  } catch (_) { /* ignore quota errors */ }
}

function loadClustersFromPersistence() {
  try {
    const raw = window.localStorage.getItem(CLUSTERS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function toUniqueSortedStringArray(values) {
  return Array.from(new Set((Array.isArray(values) ? values : [])
    .map((value) => String(value || '').trim())
    .filter(Boolean))).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

function normalizePrivilegeCollection(value) {
  if (Array.isArray(value)) return toUniqueSortedStringArray(value);
  if (!value || typeof value !== 'object') return [];

  if (Array.isArray(value.privileges)) return toUniqueSortedStringArray(value.privileges);
  if (Array.isArray(value.permissions)) return toUniqueSortedStringArray(value.permissions);
  if (Array.isArray(value.perms)) return toUniqueSortedStringArray(value.perms);

  return toUniqueSortedStringArray(
    Object.entries(value)
      .filter(([, enabled]) => enabled === true || enabled === 1 || String(enabled).trim() === '1')
      .map(([privilege]) => privilege)
  );
}

function normalizeClusterPermissions(cluster) {
  const source = cluster && typeof cluster === 'object' ? cluster : {};
  const byPath = {};

  const permissionRows = Array.isArray(source.permissions) ? source.permissions : [];
  permissionRows.forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    const path = String(entry.path || entry.aclPath || entry.resource || '/').trim() || '/';
    byPath[path] = toUniqueSortedStringArray([...(byPath[path] || []), ...normalizePrivilegeCollection(entry.privileges)]);
  });

  const paths = Object.keys(byPath).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  const byPrivilege = {};
  paths.forEach((path) => {
    byPath[path].forEach((privilege) => {
      if (!byPrivilege[privilege]) byPrivilege[privilege] = [];
      byPrivilege[privilege].push(path);
    });
  });
  Object.keys(byPrivilege).forEach((privilege) => {
    byPrivilege[privilege] = toUniqueSortedStringArray(byPrivilege[privilege]);
  });

  return {
    clusterId: String(source.clusterId || source.id || '').trim(),
    clusterName: String(source.clusterName || source.name || '').trim(),
    fetchedAt: source.fetchedAt || null,
    error: source.error ? String(source.error) : '',
    permissions: paths.map((path) => ({ path, privileges: byPath[path] })),
    byPath,
    paths,
    grantedPrivileges: Object.keys(byPrivilege).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
    byPrivilege,
  };
}

function normalizeProxmoxPermissionsPayload(payload) {
  const source = payload && typeof payload === 'object' && payload.data !== undefined
    ? payload.data
    : payload;

  // New backend format:
  // { fetchedAt, clusters: [{ clusterId, clusterName, fetchedAt, permissions: [{ path, privileges }], error? }] }
  if (source && typeof source === 'object' && Array.isArray(source.clusters)) {
    const clusters = source.clusters.map(normalizeClusterPermissions);
    const byPath = {};

    clusters.forEach((cluster) => {
      Object.entries(cluster.byPath).forEach(([path, privileges]) => {
        byPath[path] = toUniqueSortedStringArray([...(byPath[path] || []), ...privileges]);
      });
    });

    const paths = Object.keys(byPath).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    const byPrivilege = {};
    paths.forEach((path) => {
      byPath[path].forEach((privilege) => {
        if (!byPrivilege[privilege]) byPrivilege[privilege] = [];
        byPrivilege[privilege].push(path);
      });
    });
    Object.keys(byPrivilege).forEach((privilege) => {
      byPrivilege[privilege] = toUniqueSortedStringArray(byPrivilege[privilege]);
    });

    return {
      fetchedAt: source.fetchedAt || null,
      clusters,
      byPath,
      paths,
      grantedPrivileges: Object.keys(byPrivilege).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
      byPrivilege,
      updatedAt: Date.now(),
    };
  }

  const byPath = {};
  const addPathPrivileges = (rawPath, privileges) => {
    const path = String(rawPath || '/').trim() || '/';
    byPath[path] = toUniqueSortedStringArray([...(byPath[path] || []), ...normalizePrivilegeCollection(privileges)]);
  };

  if (Array.isArray(source)) {
    source.forEach((entry) => {
      if (!entry || typeof entry !== 'object') return;
      addPathPrivileges(
        entry.path || entry.aclPath || entry.resource || '/',
        entry.privileges ?? entry.permissions ?? entry.perms ?? entry
      );
    });
  } else if (source && typeof source === 'object') {
    Object.entries(source).forEach(([path, value]) => {
      addPathPrivileges(path, value);
    });
  }

  const paths = Object.keys(byPath).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  const byPrivilege = {};

  paths.forEach((path) => {
    byPath[path].forEach((privilege) => {
      if (!byPrivilege[privilege]) byPrivilege[privilege] = [];
      byPrivilege[privilege].push(path);
    });
  });

  Object.keys(byPrivilege).forEach((privilege) => {
    byPrivilege[privilege] = toUniqueSortedStringArray(byPrivilege[privilege]);
  });

  return {
    fetchedAt: source?.fetchedAt || null,
    clusters: [],
    byPath,
    paths,
    grantedPrivileges: Object.keys(byPrivilege).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
    byPrivilege,
    updatedAt: Date.now(),
  };
}

const state = {
  proxmoxClusters: loadClustersFromPersistence(),
  vmConfs: [],
  vmStats: [],
  vmSamples: {}, // `${clusterId}:${vmid}` -> { netin, netout, ts }
  vmPrevSamples: {}, // `${clusterId}:${vmid}` -> { netin, netout, ts }
  vmRates: {}, // `${clusterId}:${vmid}` -> { inBps, outBps }
  proxmoxPermissions: normalizeProxmoxPermissionsPayload(null),
  virtualMachines: [],
  resourcePools: [],
  // Guest-Agent-Daten (NICs/IPs, OS) aus dem guestInfo-Event; Key `${clusterId}:${vmid}`.
  vmGuestInfo: {},
  // Globale Proxmox-Tasks aus dem SSE-Stream (init-Snapshot + tasks-Deltas).
  globalTasks: [],
  globalTasksMeta: { limit: 100, clustersQueried: [], clusterErrors: [] },
  // Admin-Infrastruktur-Snapshot (Cluster/Nodes/Storages/Ceph/HA) — nur für Admin-Sessions gefüllt.
  infrastructureClusters: [],
  loading: false,
  _sseSource: null,
};

function rebuildVirtualMachines(state) {
  const statsMap = new Map((state.vmStats || []).map((s) => [`${s.clusterId}:${s.vmid}`, s]));
  state.virtualMachines = (state.vmConfs || []).map((conf) => {
    const key = `${conf.clusterId}:${conf.vmid}`;
    const stat = statsMap.get(key) || {};
    const rate = state.vmRates[key] || {};
    const guestInfo = state.vmGuestInfo[key] || null;
    return { ...conf, ...stat, ...rate, guestInfo };
  });
}

function applyGlobalTasksMeta(state, payload) {
  const meta = { ...state.globalTasksMeta };
  if (payload?.limit != null) meta.limit = Number(payload.limit) || 100;
  if (Array.isArray(payload?.clustersQueried)) meta.clustersQueried = payload.clustersQueried;
  if (Array.isArray(payload?.clusterErrors)) meta.clusterErrors = payload.clusterErrors;
  state.globalTasksMeta = meta;
}

// Update per-VM samples from an incoming (possibly partial) stats batch and
// recompute rates for the VMs included in this batch. Untouched VMs keep their
// previous rate so the topology view stays stable across staggered updates.
function ingestVmStats(state, stats) {
  const now = Date.now() / 1000;
  const nextSamples = { ...state.vmSamples };
  const nextPrev = { ...state.vmPrevSamples };
  const nextRates = { ...state.vmRates };
  stats.forEach((s) => {
    const key = `${s.clusterId}:${s.vmid}`;
    const netin = Number(s.netin || 0);
    const netout = Number(s.netout || 0);
    const diskread = Number(s.diskread || 0);
    const diskwrite = Number(s.diskwrite || 0);
    const curr = nextSamples[key];
    // Counters unchanged → Proxmox poller hasn't ticked; keep last sample/rate.
    if (curr && curr.netin === netin && curr.netout === netout
      && curr.diskread === diskread && curr.diskwrite === diskwrite) return;
    const sample = { netin, netout, diskread, diskwrite, ts: now };
    if (curr) nextPrev[key] = curr;
    nextSamples[key] = sample;
    const prev = nextPrev[key];
    if (prev) {
      const dt = sample.ts - prev.ts;
      if (dt > 0) {
        const inB = netin - prev.netin;
        const outB = netout - prev.netout;
        const readB = diskread - Number(prev.diskread || 0);
        const writeB = diskwrite - Number(prev.diskwrite || 0);
        nextRates[key] = {
          inBps: inB > 0 ? inB / dt : 0,
          outBps: outB > 0 ? outB / dt : 0,
          diskReadBps: readB > 0 ? readB / dt : 0,
          diskWriteBps: writeB > 0 ? writeB / dt : 0,
        };
      }
    }
  });
  state.vmSamples = nextSamples;
  state.vmPrevSamples = nextPrev;
  state.vmRates = nextRates;
}

function normalizeAclPath(rawPath) {
  const normalized = String(rawPath || '').trim();
  if (!normalized) return '/';
  const withLeadingSlash = normalized.startsWith('/') ? normalized : `/${normalized}`;
  if (withLeadingSlash.length > 1 && withLeadingSlash.endsWith('/')) {
    return withLeadingSlash.replace(/\/+$/, '');
  }
  return withLeadingSlash;
}

function doesAclPathApplyToTarget(aclPath, targetPath) {
  const normalizedAclPath = normalizeAclPath(aclPath);
  const normalizedTargetPath = normalizeAclPath(targetPath);
  if (normalizedAclPath === '/') return true;
  if (normalizedTargetPath === normalizedAclPath) return true;
  return normalizedTargetPath.startsWith(`${normalizedAclPath}/`);
}

function resolveVmIdentityFromInput(state, vmRef) {
  const source = vmRef && typeof vmRef === 'object' ? vmRef : {};
  const clusterId = String(source.clusterId || source.cluster || '').trim();
  const vmid = Number(source.vmid ?? source.id ?? source.vmId);

  if (!clusterId || !Number.isInteger(vmid) || vmid <= 0) {
    return { clusterId: '', vmid: null, poolId: '' };
  }

  const listedVm = (state.vmConfs || []).find((vm) =>
    String(vm?.clusterId || '').trim() === clusterId && Number(vm?.vmid) === vmid
  );

  const poolId = String(
    source.pool
    || source.poolid
    || source.resourcePool
    || listedVm?.pool
    || listedVm?.poolid
    || ''
  ).trim();

  return { clusterId, vmid, poolId };
}

function collectClusterPathPrivileges(state, clusterId) {
  const clusters = Array.isArray(state.proxmoxPermissions?.clusters) ? state.proxmoxPermissions.clusters : [];
  const cluster = clusters.find((entry) => String(entry?.clusterId || '').trim() === String(clusterId || '').trim());
  if (cluster && cluster.byPath && typeof cluster.byPath === 'object') return cluster.byPath;

  // Fallback for legacy flattened payloads without clusters[]
  const fallback = state.proxmoxPermissions?.byPath;
  return fallback && typeof fallback === 'object' ? fallback : {};
}

function buildVmTargetPaths({ vmid, poolId }) {
  const paths = [`/vms/${vmid}`];
  const normalizedPoolId = String(poolId || '').trim();
  if (normalizedPoolId) {
    paths.push(`/pool/${normalizedPoolId}`);
  }
  return paths.map(normalizeAclPath);
}

function resolveVmPrivilegesFromState(state, vmRef) {
  const identity = resolveVmIdentityFromInput(state, vmRef);
  if (!identity.clusterId || !identity.vmid) {
    return {
      clusterId: identity.clusterId,
      vmid: identity.vmid,
      poolId: identity.poolId,
      vmPath: identity.vmid ? `/vms/${identity.vmid}` : '',
      poolPath: identity.poolId ? `/pool/${identity.poolId}` : '',
      targetPaths: [],
      matchedPaths: [],
      privileges: [],
      hasPrivilege: () => false,
    };
  }

  const byPath = collectClusterPathPrivileges(state, identity.clusterId);
  const targetPaths = buildVmTargetPaths(identity);
  const matchedPaths = [];

  Object.entries(byPath).forEach(([aclPath, privileges]) => {
    const normalizedAclPath = normalizeAclPath(aclPath);
    const appliesToVm = doesAclPathApplyToTarget(normalizedAclPath, `/vms/${identity.vmid}`);
    const appliesToPool = identity.poolId
      ? doesAclPathApplyToTarget(normalizedAclPath, `/pool/${identity.poolId}`)
      : false;
    if (!appliesToVm && !appliesToPool) return;

    matchedPaths.push({
      path: normalizedAclPath,
      privileges: toUniqueSortedStringArray(privileges),
      scope: appliesToVm ? 'vm' : 'pool',
    });
  });

  matchedPaths.sort((a, b) => {
    if (a.path.length !== b.path.length) return b.path.length - a.path.length;
    return a.path.localeCompare(b.path, undefined, { sensitivity: 'base' });
  });

  const privilegeSet = new Set();
  matchedPaths.forEach((entry) => {
    entry.privileges.forEach((privilege) => privilegeSet.add(privilege));
  });
  const privileges = Array.from(privilegeSet).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

  return {
    clusterId: identity.clusterId,
    vmid: identity.vmid,
    poolId: identity.poolId,
    vmPath: `/vms/${identity.vmid}`,
    poolPath: identity.poolId ? `/pool/${identity.poolId}` : '',
    targetPaths,
    matchedPaths,
    privileges,
    hasPrivilege: (privilege) => privileges.includes(String(privilege || '').trim()),
  };
}

const mutations = {
  SET_PROXMOX_CLUSTERS(state, clusters) {
    state.proxmoxClusters = normalizeClusterList(clusters);
    saveClustersToPersistence(state.proxmoxClusters);
  },
  SET_VM_CONFS(state, confs) {
    state.vmConfs = Array.isArray(confs) ? confs : [];
    rebuildVirtualMachines(state);
  },
  APPLY_VM_CONF_DELTA(state, { changed = [], removed = [] }) {
    const map = new Map(state.vmConfs.map((vm) => [`${vm.clusterId}:${vm.vmid}`, vm]));
    changed.forEach((vm) => map.set(`${vm.clusterId}:${vm.vmid}`, vm));
    removed.forEach((key) => map.delete(key));
    state.vmConfs = Array.from(map.values());
    rebuildVirtualMachines(state);
  },
  SET_VM_STATS(state, stats) {
    const incoming = Array.isArray(stats) ? stats : [];
    // Merge incoming stats into the existing vmStats map (per-cluster batches keep other clusters intact).
    const map = new Map((state.vmStats || []).map((s) => [`${s.clusterId}:${s.vmid}`, s]));
    incoming.forEach((s) => map.set(`${s.clusterId}:${s.vmid}`, s));
    state.vmStats = Array.from(map.values());
    ingestVmStats(state, incoming);
    rebuildVirtualMachines(state);
  },
  SET_RESOURCE_POOLS(state, resourcePools) {
    state.resourcePools = Array.isArray(resourcePools) ? resourcePools : [];
  },
  SET_PROXMOX_PERMISSIONS(state, permissionsPayload) {
    state.proxmoxPermissions = normalizeProxmoxPermissionsPayload(permissionsPayload);
  },
  SET_INFRASTRUCTURE_CLUSTERS(state, clusters) {
    state.infrastructureClusters = Array.isArray(clusters) ? clusters : [];
  },
  SET_VM_GUEST_INFO(state, vms) {
    // Immer kompletter Snapshot — letzten Stand ersetzen.
    const map = {};
    (Array.isArray(vms) ? vms : []).forEach((entry) => {
      const clusterId = String(entry?.clusterId || '').trim();
      const vmid = entry?.vmid;
      if (!clusterId || vmid == null) return;
      map[`${clusterId}:${vmid}`] = {
        os: entry?.os || null,
        nics: Array.isArray(entry?.nics) ? entry.nics : [],
        agent: entry?.agent || null,
      };
    });
    state.vmGuestInfo = map;
    rebuildVirtualMachines(state);
  },
  SET_GLOBAL_TASKS(state, payload) {
    state.globalTasks = Array.isArray(payload?.tasks) ? payload.tasks : [];
    applyGlobalTasksMeta(state, payload);
  },
  APPLY_TASKS_DELTA(state, payload) {
    const map = new Map(state.globalTasks.map((t) => [`${t.clusterId}:${t.upid}`, t]));
    (Array.isArray(payload?.changed) ? payload.changed : []).forEach((t) => map.set(`${t.clusterId}:${t.upid}`, t));
    (Array.isArray(payload?.removed) ? payload.removed : []).forEach((key) => map.delete(key));
    state.globalTasks = Array.from(map.values());
    applyGlobalTasksMeta(state, payload);
  },
  APPLY_POOL_DELTA(state, { changed = [], removed = [] }) {
    const map = new Map(state.resourcePools.map((p) => [`${p.clusterId}:${p.poolid}`, p]));
    changed.forEach((p) => map.set(`${p.clusterId}:${p.poolid}`, p));
    removed.forEach((key) => map.delete(key));
    state.resourcePools = Array.from(map.values());
  },
  SET_LOADING(state, loading) {
    state.loading = Boolean(loading);
  },
  CLEAR_PROXMOX_CLUSTERS(state) {
    state.proxmoxClusters = [];
    state.vmConfs = [];
    state.vmStats = [];
    state.proxmoxPermissions = normalizeProxmoxPermissionsPayload(null);
    state.virtualMachines = [];
    state.resourcePools = [];
    state.vmGuestInfo = {};
    state.globalTasks = [];
    state.globalTasksMeta = { limit: 100, clustersQueried: [], clusterErrors: [] };
    state.infrastructureClusters = [];
    state.loading = false;
    try { window.localStorage.removeItem(CLUSTERS_STORAGE_KEY); } catch (_) { /* ignore */ }
  },
  SET_SSE_SOURCE(state, source) {
    state._sseSource = source;
  },
};

const actions = {
  setProxmoxClusters({ commit }, clusters) {
    commit('SET_PROXMOX_CLUSTERS', clusters);
  },
  async fetchVirtualMachines({ commit }) {
    const response = await axios.get(`${API_BASE_URL}/virtual-servers/vms`, {
      withCredentials: true,
    });
    commit('SET_VM_CONFS', response.data?.vmConfs || response.data?.virtualMachines || []);
  },
  async fetchResourcePools({ commit }) {
    const response = await axios.get(`${API_BASE_URL}/virtual-servers/pools`, {
      withCredentials: true,
    });
    commit('SET_RESOURCE_POOLS', response.data?.resourcePools || []);
  },
  async fetchVirtualInfrastructureData({ dispatch, commit, state }) {
    commit('SET_LOADING', true);
    try {
      await Promise.all([
        dispatch('fetchVirtualMachines'),
        dispatch('fetchResourcePools'),
      ]);
      // If clusters were not populated (e.g. after page reload), derive them from VM/pool data
      if (!state.proxmoxClusters.length) {
        const clusterMap = new Map();
        [...state.vmConfs, ...state.resourcePools].forEach((item) => {
          const cid = item?.clusterId;
          if (cid && !clusterMap.has(cid)) {
            clusterMap.set(cid, { id: cid, name: item.clusterName || cid });
          }
        });
        commit('SET_PROXMOX_CLUSTERS', Array.from(clusterMap.values()));
      }
    } finally {
      commit('SET_LOADING', false);
    }
  },
      async forceSessionLogout({ dispatch, commit, state }) {
        if (sseLogoutInProgress) return;
        sseLogoutInProgress = true;

        clearSseReconnectTimer();
        clearSseInactivityTimer();
        clearAppIdleLogoutTimer();
        clearAppIdleWarningTimer();
        sseReconnectAttempts = 0;

        if (state._sseSource) {
          state._sseSource.close();
        }
        commit('SET_SSE_SOURCE', null);
    commit('CLEAR_PROXMOX_CLUSTERS');

        await dispatch('stopRuntimeGuards');
        dispatch('auth/clearApiSession', null, { root: true });
        commit('auth/SET_CURRENT_USER', null, { root: true });

        try {
          // Theme-Wahl und gemerkter Benutzername sind rein clientseitig und sollen den Logout überleben.
          const themePreference = window.localStorage.getItem('theme-preference');
          const layoutMode = window.localStorage.getItem('layout-mode');
          const lastUsername = window.localStorage.getItem('login.lastUsername');
          window.localStorage.clear();
          window.sessionStorage.clear();
          if (themePreference) window.localStorage.setItem('theme-preference', themePreference);
          if (layoutMode) window.localStorage.setItem('layout-mode', layoutMode);
          if (lastUsername) window.localStorage.setItem('login.lastUsername', lastUsername);
        } catch (_) {
          // ignore storage access failures
        }

        const currentPath = `${window.location.pathname || ''}${window.location.search || ''}${window.location.hash || ''}`;
        const redirectQuery = currentPath && !String(window.location.pathname || '').startsWith('/login')
          ? `?redirectFrom=${encodeURIComponent(currentPath)}`
          : '';
        window.location.replace(`/login${redirectQuery}`);
      },

      async verifyApiSession({ rootGetters }) {
        const csrfToken = rootGetters['auth/csrfPreventionToken'];
        if (!csrfToken) return false;
        try {
          await axios.get(`${API_BASE_URL}/user/profile`, {
            headers: { 'X-CSRF-Prevention-Token': csrfToken },
            withCredentials: true,
          });
          return true;
        } catch (_) {
          return false;
        }
      },

      scheduleSseInactivityWatchdog({ dispatch, rootGetters }) {
        if (!rootGetters['auth/isApiAuthenticated']) return;
        clearSseInactivityTimer();
        sseInactivityTimer = window.setTimeout(() => {
          dispatch('handleSseDisconnect', { reason: 'inactive' });
        }, SSE_INACTIVITY_TIMEOUT_MS);
      },

      handleSseActivity({ dispatch, rootGetters }) {
        if (!rootGetters['auth/isApiAuthenticated']) return;
        sseReconnectAttempts = 0;
        clearSseReconnectTimer();
        dispatch('scheduleSseInactivityWatchdog');
      },

      async handleSseDisconnect({ dispatch, commit, state, rootGetters }, { reason = 'disconnect' } = {}) {
        if (!rootGetters['auth/isApiAuthenticated'] || sseLogoutInProgress) return;

        clearSseInactivityTimer();

        if (state._sseSource) {
          state._sseSource.close();
          commit('SET_SSE_SOURCE', null);
        }

        if (sseReconnectTimer) return;

        sseReconnectAttempts += 1;

        if (sseReconnectAttempts > SSE_MAX_RECONNECT_ATTEMPTS) {
          const sessionAlive = await dispatch('verifyApiSession');
          if (!sessionAlive) {
            await dispatch('forceSessionLogout');
            return;
          }
          await dispatch('forceSessionLogout');
          return;
        }

        const delay = Math.min(
          SSE_RECONNECT_BASE_DELAY_MS * (2 ** (sseReconnectAttempts - 1)),
          SSE_RECONNECT_MAX_DELAY_MS,
        );

        sseReconnectTimer = window.setTimeout(async () => {
          clearSseReconnectTimer();
          if (!rootGetters['auth/isApiAuthenticated'] || sseLogoutInProgress) return;

          const sessionAlive = await dispatch('verifyApiSession');
          if (!sessionAlive) {
            await dispatch('forceSessionLogout');
            return;
          }

          dispatch('startSseStream');
        }, delay);

        if (process.env.NODE_ENV !== 'production') {
          console.warn(`[SSE] Stream unterbrochen (${reason}); Reconnect in ${delay}ms (Versuch ${sseReconnectAttempts}/${SSE_MAX_RECONNECT_ATTEMPTS})`);
        }
      },

      async initRuntimeGuards({ dispatch }) {
        if (runtimeGuardsInitialized || typeof window === 'undefined' || typeof document === 'undefined') return;
        runtimeGuardsInitialized = true;

        runtimeVisibilityHandler = () => {
          dispatch('handleVisibilityChange', document.visibilityState);
        };
        runtimeOnlineHandler = () => {
          dispatch('handleNetworkOnline');
        };
        runtimeActivityHandler = () => {
          dispatch('recordAppActivity');
        };

        document.addEventListener('visibilitychange', runtimeVisibilityHandler);
        window.addEventListener('online', runtimeOnlineHandler);
        APP_ACTIVITY_EVENTS.forEach((eventName) => {
          window.addEventListener(eventName, runtimeActivityHandler, { passive: true });
        });

        dispatch('recordAppActivity');
      },

      async stopRuntimeGuards() {
        clearSseReconnectTimer();
        clearSseInactivityTimer();
        clearAppIdleLogoutTimer();
        clearAppIdleWarningTimer();

        if (runtimeVisibilityHandler) {
          document.removeEventListener('visibilitychange', runtimeVisibilityHandler);
          runtimeVisibilityHandler = null;
        }
        if (runtimeOnlineHandler) {
          window.removeEventListener('online', runtimeOnlineHandler);
          runtimeOnlineHandler = null;
        }
        if (runtimeActivityHandler) {
          APP_ACTIVITY_EVENTS.forEach((eventName) => {
            window.removeEventListener(eventName, runtimeActivityHandler);
          });
          runtimeActivityHandler = null;
        }
        runtimeGuardsInitialized = false;
      },

      recordAppActivity({ dispatch, rootGetters }) {
        if (!rootGetters['auth/isApiAuthenticated'] || sseLogoutInProgress) return;
        clearAppIdleWarningTimer();
        clearAppIdleLogoutTimer();
        appIdleWarningTimer = window.setTimeout(() => {
          if (!rootGetters['auth/isApiAuthenticated'] || sseLogoutInProgress) return;
          AppSwal.fire({
            toast: true,
            position: 'top-end',
            icon: 'warning',
            title: 'Automatischer Logout in 1 Minute',
            text: 'Bitte kurz in der Applikation interagieren, um angemeldet zu bleiben.',
            showConfirmButton: false,
            timer: 12000,
            timerProgressBar: true,
          });
        }, APP_IDLE_WARNING_TIMEOUT_MS);
        appIdleLogoutTimer = window.setTimeout(() => {
          dispatch('forceSessionLogout');
        }, APP_IDLE_LOGOUT_TIMEOUT_MS);
      },

      handleVisibilityChange({ dispatch, state, rootGetters }, visibilityState) {
        if (!rootGetters['auth/isApiAuthenticated'] || visibilityState !== 'visible') return;
        const readyState = state._sseSource?.readyState;
        if (!state._sseSource || readyState === 2) {
          dispatch('startSseStream');
        }
      },

      handleNetworkOnline({ dispatch, rootGetters }) {
        if (!rootGetters['auth/isApiAuthenticated']) return;
        dispatch('startSseStream');
      },

      async clearProxmoxClusters({ dispatch, commit, state }) {
        await dispatch('stopRuntimeGuards');
        clearSseReconnectTimer();
        clearSseInactivityTimer();
        clearAppIdleLogoutTimer();
        clearAppIdleWarningTimer();
        sseReconnectAttempts = 0;
        sseLogoutInProgress = false;
        if (state._sseSource) {
          state._sseSource.close();
        }
        commit('SET_SSE_SOURCE', null);
        commit('CLEAR_PROXMOX_CLUSTERS');
  },

      startSseStream({ dispatch, commit, state, rootGetters }) {
        if (!rootGetters['auth/isApiAuthenticated']) return null;
        dispatch('initRuntimeGuards');
        dispatch('recordAppActivity');

    // Stream läuft app-weit: bestehende Verbindung (CONNECTING/OPEN) wiederverwenden,
    // damit Seitenwechsel keinen Reconnect auslösen. Nur geschlossene ersetzen.
    if (state._sseSource && state._sseSource.readyState !== 2) {
      dispatch('scheduleSseInactivityWatchdog');
      return state._sseSource;
    }
    if (state._sseSource) {
      state._sseSource.close();
      commit('SET_SSE_SOURCE', null);
    }

    const es = new EventSource(`${API_BASE_URL}/virtual-servers/stream`, { withCredentials: true });
    commit('SET_SSE_SOURCE', es);
  dispatch('scheduleSseInactivityWatchdog');

    const applyClusterDefaults = (confs, pools) => {
      // Build map from existing clusters to preserve all metadata (location, hardware, etc.)
      const clusterMap = new Map(state.proxmoxClusters.map((c) => [c.id, c]));
      let changed = false;
      [...confs, ...pools].forEach((item) => {
        const cid = item?.clusterId;
        if (cid && !clusterMap.has(cid)) {
          clusterMap.set(cid, { id: cid, name: item.clusterName || cid });
          changed = true;
        }
      });
      if (changed) {
        commit('SET_PROXMOX_CLUSTERS', Array.from(clusterMap.values()));
      }
    };

    es.addEventListener('open', () => {
      dispatch('handleSseActivity');
    });

    es.addEventListener('init', (e) => {
      dispatch('handleSseActivity');
      const payload = JSON.parse(e.data);
      const { vmConfs = [], vmStats = [], resourcePools = [] } = payload;
      commit('SET_VM_CONFS', vmConfs);
      commit('SET_VM_STATS', vmStats);
      commit('SET_RESOURCE_POOLS', resourcePools);
      commit('SET_PROXMOX_PERMISSIONS', payload?.permissions ?? payload?.proxmoxPermissions ?? payload?.acl ?? null);
      if (payload?.tasks) commit('SET_GLOBAL_TASKS', payload.tasks);
      commit('SET_LOADING', false);
      applyClusterDefaults(vmConfs, resourcePools);
    });

    es.addEventListener('tasks', (e) => {
      dispatch('handleSseActivity');
      commit('APPLY_TASKS_DELTA', JSON.parse(e.data));
    });

    es.addEventListener('vmConf', (e) => {
      dispatch('handleSseActivity');
      const delta = JSON.parse(e.data);
      commit('APPLY_VM_CONF_DELTA', delta);
      applyClusterDefaults(delta.changed || [], state.resourcePools);
    });

    es.addEventListener('vmStat', (e) => {
      dispatch('handleSseActivity');
      const { stats = [] } = JSON.parse(e.data);
      commit('SET_VM_STATS', stats);
    });

    es.addEventListener('pools', (e) => {
      dispatch('handleSseActivity');
      const delta = JSON.parse(e.data);
      commit('APPLY_POOL_DELTA', delta);
      applyClusterDefaults(state.vmConfs, delta.changed || []);
    });

    es.addEventListener('permissions', (e) => {
      dispatch('handleSseActivity');
      const payload = JSON.parse(e.data);
      commit('SET_PROXMOX_PERMISSIONS', payload?.permissions ?? payload?.proxmoxPermissions ?? payload?.acl ?? payload ?? null);
    });

    // Nur Admin-Sessions erhalten dieses Event (kompletter Snapshot alle 30 s).
    es.addEventListener('infrastructure', (e) => {
      dispatch('handleSseActivity');
      const payload = JSON.parse(e.data);
      commit('SET_INFRASTRUCTURE_CLUSTERS', payload?.clusters ?? []);
    });

    // Guest-Agent-Daten (bis ~5 Min alt): initial nach init, danach nur bei Änderungen.
    es.addEventListener('guestInfo', (e) => {
      dispatch('handleSseActivity');
      const payload = JSON.parse(e.data);
      commit('SET_VM_GUEST_INFO', payload?.vms ?? []);
    });

    es.addEventListener('error', (e) => {
      if (!e.data) {
        dispatch('handleSseDisconnect', { reason: 'eventsource-error' });
        return;
      }
      // Proxmox poller error — connection stays open, next poll in 3s
      console.warn('[SSE] Poller-Fehler:', JSON.parse(e.data)?.message);
    });

    return es;
  },

  stopSseStream({ commit, state }) {
    clearSseReconnectTimer();
    clearSseInactivityTimer();
    sseReconnectAttempts = 0;
    if (state._sseSource) {
      state._sseSource.close();
    }
    commit('SET_SSE_SOURCE', null);
  },
};

const getters = {
  proxmoxClusters: (state) => state.proxmoxClusters,
  virtualMachines: (state) => state.virtualMachines,
  resourcePools: (state) => state.resourcePools,
  infrastructureClusters: (state) => state.infrastructureClusters,
  vmGuestInfoMap: (state) => state.vmGuestInfo,
  globalTasks: (state) => state.globalTasks,
  globalTasksMeta: (state) => state.globalTasksMeta,
  proxmoxPermissions: (state) => state.proxmoxPermissions,
  vmPrivileges: (state) => (vmRef) => resolveVmPrivilegesFromState(state, vmRef),
  hasVmPrivilege: (state) => (vmRef, privilege) => {
    const resolved = resolveVmPrivilegesFromState(state, vmRef);
    return resolved.hasPrivilege(privilege);
  },
  loading: (state) => state.loading,
  sseSource: (state) => state._sseSource,
  hierarchy: (state, getters, rootState, rootGetters) => {
    const canAccessAdmin = Boolean(rootGetters?.['auth/canAccessAdmin']);
    const clusters = Array.isArray(state.proxmoxClusters) ? state.proxmoxClusters : [];
    const vms = Array.isArray(state.vmConfs) ? state.vmConfs : [];
    const pools = (Array.isArray(state.resourcePools) ? state.resourcePools : [])
      .filter((pool) => canAccessAdmin || !isTemplatesPoolBranch(pool?.poolid));

    const mappedClusters = clusters.map((cluster) => {
      const clusterId = cluster?.id;

      const clusterPools = pools.filter((pool) => pool?.clusterId === clusterId);
      const poolMap = new Map();
      const rootPools = [];

      clusterPools.forEach((pool) => {
        const fullPoolId = String(pool?.poolid || '');
        if (!fullPoolId) return;

        poolMap.set(fullPoolId, {
          ...pool,
          type: 'resourcePool',
          name: getPoolLeafName(fullPoolId),
          subitems: [],
        });
      });

      poolMap.forEach((poolNode, fullPoolId) => {
        const slashIndex = fullPoolId.lastIndexOf('/');
        const parentId = slashIndex >= 0 ? fullPoolId.slice(0, slashIndex) : null;

        if (parentId && poolMap.has(parentId)) {
          poolMap.get(parentId).subitems.push(poolNode);
        } else {
          rootPools.push(poolNode);
        }
      });

      const clusterVms = vms
        .filter((vm) => vm?.clusterId === clusterId)
        .filter((vm) => canAccessAdmin || !isTemplatesPoolBranch(vm?.pool || vm?.poolid))
        .map((vm) => ({
          ...vm,
          type: 'vm',
          subitems: [],
        }));

      const unassignedVms = [];
      clusterVms.forEach((vmNode) => {
        const vmPoolId = String(vmNode?.pool || vmNode?.poolid || '');
        if (vmPoolId && poolMap.has(vmPoolId)) {
          poolMap.get(vmPoolId).subitems.push(vmNode);
        } else {
          unassignedVms.push(vmNode);
        }
      });

      const sortNodes = (nodes) => {
        nodes.sort((a, b) => {
          if (a?.type !== b?.type) {
            return a?.type === 'resourcePool' ? -1 : 1;
          }
          return sortByLabel(a, b);
        });
        nodes.forEach((node) => {
          if (Array.isArray(node?.subitems) && node.subitems.length) {
            sortNodes(node.subitems);
          }
        });
      };

      const subitems = [...rootPools, ...unassignedVms];
      sortNodes(subitems);

      return {
        ...cluster,
        type: 'cluster',
        subitems,
      };
    });

    return mappedClusters.sort(sortByLabel);
  },
  combinedHierarchy: (state, getters, rootState, rootGetters) => {
    const canAccessAdmin = Boolean(rootGetters?.['auth/canAccessAdmin']);
    const vms = Array.isArray(state.vmConfs) ? state.vmConfs : [];
    const pools = (Array.isArray(state.resourcePools) ? state.resourcePools : [])
      .filter((pool) => canAccessAdmin || !isTemplatesPoolBranch(pool?.poolid));

    // Merge pools across clusters by poolid path. Same poolid in two clusters
    // collapses into one node; its subitems contain VMs from both clusters.
    const poolMap = new Map();
    pools.forEach((pool) => {
      const fullPoolId = String(pool?.poolid || '');
      if (!fullPoolId) return;
      if (!poolMap.has(fullPoolId)) {
        poolMap.set(fullPoolId, {
          poolid: fullPoolId,
          type: 'resourcePool',
          name: getPoolLeafName(fullPoolId),
          clusterId: '',
          combined: true,
          subitems: [],
        });
      }
    });

    const rootPools = [];
    poolMap.forEach((poolNode, fullPoolId) => {
      const slashIndex = fullPoolId.lastIndexOf('/');
      const parentId = slashIndex >= 0 ? fullPoolId.slice(0, slashIndex) : null;
      if (parentId && poolMap.has(parentId)) {
        poolMap.get(parentId).subitems.push(poolNode);
      } else {
        rootPools.push(poolNode);
      }
    });

    const unassignedVms = [];

    vms.forEach((vm) => {
      const vmPoolId = String(vm?.pool || vm?.poolid || '');
      if (!canAccessAdmin && isTemplatesPoolBranch(vmPoolId)) return;
      if (vmPoolId && poolMap.has(vmPoolId)) {
        const vmNode = { ...vm, type: 'vm', subitems: [] };
        poolMap.get(vmPoolId).subitems.push(vmNode);
      } else {
        unassignedVms.push({ ...vm, type: 'vm', subitems: [] });
      }
    });

    const sortNodes = (nodes) => {
      nodes.sort((a, b) => {
        if (a?.type !== b?.type) {
          return a?.type === 'resourcePool' ? -1 : 1;
        }
        return sortByLabel(a, b);
      });
      nodes.forEach((node) => {
        if (Array.isArray(node?.subitems) && node.subitems.length) {
          sortNodes(node.subitems);
        }
      });
    };

    const rootItems = [...rootPools, ...unassignedVms];
    sortNodes(rootItems);

    return rootItems;
  },
};

export { state, mutations, actions, getters };
